// =============================================================
// DealNoteComposer — "Write a note" on a deal, capital item or task.
//
// It is the Notes-app editor (NoteBodyEditor: the inline calculator, Harvi
// writing tools, images / sketches / files inside the note) with the deal
// composer's own controls kept: Scan, Scan image or PDF, Write (handwriting),
// Attach (a document filed under the note and summarized by AI), Date
// (back-dating) and Author (entered on behalf of a colleague), then Post.
//
// WHILE IT IS OPEN THE NOTE IS A PRIVATE NOTEBOOK NOTE. It is created on the
// first keystroke (or the first file) with an id this browser chose, saved as
// you type, and visible to nobody else — so files have a note to live in,
// nothing is lost if the page closes (it is reopened here, and it is under
// Private in the Notes app), and colleagues never watch a half-written note.
// POST turns it into the record's note: POST /api/notes { publishNoteId }
// links, dates and attributes it, and runs the ordinary note path — the deal's
// leads, the task's people and @mentions are told once, with the whole note.
// Discard moves the private note to Recently deleted.
// =============================================================
import { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { UserContext } from '../../context/UserContext';
import { getAccessToken } from '../../config/auth';
import { Btn, Icon, useToast } from '../ui';
import { FixedScopeProjectPicker } from './DealScopePicker';
import { NoteBodyEditor, insertMarkdownAtEnd } from './NoteBodyEditor';
import { InkCanvas } from './InkCanvas';
import WritingTools from '../../notebook/WritingTools';
import { uploadNotebookFile, makeNotebookFileResolver } from '../../notebook/files';
import { prepareFileForOcr, isPdfFile, ocrFileNoun, runHandwritingOcr } from '../../utils/handwritingOcr';
import { todayIsoInZone } from '../../utils/format';

const SAVE_DELAY_MS = 800;
const STORE_PREFIX = 'harvus.dealComposer.';

/** The one key a scope's open draft is remembered under (this browser only). */
export const composerScopeKey = ({ taskId, dealId, dealDetailId }) =>
  taskId ? `task:${taskId}` : dealDetailId ? `detail:${dealDetailId}` : dealId ? `deal:${dealId}` : null;

export function pendingComposerNoteId(scopeKey) {
  if (!scopeKey) return null;
  try { return window.localStorage.getItem(STORE_PREFIX + scopeKey) || null; } catch { return null; }
}
function rememberNote(scopeKey, id) {
  if (!scopeKey) return;
  try {
    if (id) window.localStorage.setItem(STORE_PREFIX + scopeKey, id);
    else window.localStorage.removeItem(STORE_PREFIX + scopeKey);
  } catch { /* private window — the note is still under Private in Notes */ }
}

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0; return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  }));

const base64ToFile = (base64, name, type) => {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], name, { type });
};

const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = ev => resolve(String(ev.target.result).split(',')[1]);
  reader.onerror = () => reject(new Error('Could not read file'));
  reader.readAsDataURL(file);
});

// An Attach-button document goes straight to Blob Storage (never inside the
// note's JSON body) and is filed under the note on Post, where it gets its AI
// summary. Null = direct upload unavailable; the caller falls back to base64.
async function uploadAttachmentToBlob(file) {
  const token = await getAccessToken();
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  const r = await fetch('/api/notes/attachment-upload-url', {
    method: 'POST', headers,
    body: JSON.stringify({ fileName: file.name, contentType: file.type || 'application/octet-stream' }),
  });
  if (!r.ok) throw new Error('Could not start upload');
  const info = await r.json();
  if (!info.available) return null;
  const put = await fetch(info.sasUrl, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!put.ok) throw new Error('Upload failed');
  return { blobContainer: info.blobContainer, blobPath: info.blobPath };
}

export function DealNoteComposer({ scope, dealId, dealDetailId, users, legacyDraft, onPosted, onClose }) {
  const { toast } = useToast();
  const currentUser = useContext(UserContext);
  const scopeKey = composerScopeKey(scope);

  // The private note behind the composer: null until something is written.
  const [noteId, setNoteId] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [seed, setSeed] = useState({ content: undefined, noteDate: null });
  const [epoch, setEpoch] = useState(0);
  const [saveState, setSaveState] = useState('idle'); // idle | dirty | saving | saved | error
  const [isEmpty, setIsEmpty] = useState(true);
  const [posting, setPosting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [sketching, setSketching] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [attachments, setAttachments] = useState([]);
  const [noteDate, setNoteDate] = useState(todayIsoInZone());

  const editorRef = useRef(null);
  const noteIdRef = useRef(null);
  const creating = useRef(null);
  const pending = useRef(null);
  const timer = useRef(null);
  const savingRef = useRef(false);
  const seeded = useRef(false);
  const attachKey = useRef(0);
  const fileInput = useRef(null);
  const attachInput = useRef(null);
  const cameraInput = useRef(null);
  const scanInput = useRef(null);
  const resolveFileUrl = useMemo(() => makeNotebookFileResolver(api), []);

  // "Entered on behalf of" — defaults to me.
  const internalUsers = useMemo(() => (users || []).filter(u => u.isInternal && u.isActive !== false), [users]);
  const meUserId = useMemo(() => internalUsers.find(u => u.email === currentUser?.email)?.userID || '', [internalUsers, currentUser]);
  const [authorUserId, setAuthorUserId] = useState('');
  // ORTHOGONAL TAG (see ProjectPicker.jsx / dealProjects.js) — which of this
  // scope's Projects the note belongs to, or none. `dealId` comes from the
  // caller's own props (NOT `scope`, which for a task-scoped note is just
  // `{ taskId }`) so a task's notes still offer its parent deal's projects.
  const [projectId, setProjectId] = useState('');
  useEffect(() => { if (!authorUserId && meUserId) setAuthorUserId(meUserId); }, [meUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reopen what this browser was writing on this record, or seed from an old
  // server draft (note-drafts, from before this composer).
  useEffect(() => {
    let cancelled = false;
    const id = pendingComposerNoteId(scopeKey);
    (async () => {
      if (id) {
        try {
          const n = await api.get(`notebook/${id}`);
          if (!cancelled && n && !n.isDeleted && n.isPrivate && n.isOwner) {
            noteIdRef.current = n.id; setNoteId(n.id);
            setSeed({ content: n.content, noteDate: n.noteDate });
            if (n.noteDate) setNoteDate(n.noteDate);
            setSaveState('saved');
            setLoaded(true);
            return;
          }
        } catch { /* gone, or posted elsewhere */ }
        rememberNote(scopeKey, null);
      }
      if (!cancelled) {
        if (legacyDraft?.content) setSeed({ content: legacyDraft.content, noteDate: legacyDraft.noteDate || null });
        if (legacyDraft?.noteDate) setNoteDate(String(legacyDraft.noteDate).slice(0, 10));
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /** The private note, created the first time anything needs it. */
  const ensureNote = useCallback(async (content) => {
    if (noteIdRef.current) return noteIdRef.current;
    if (!creating.current) {
      const id = newId();
      creating.current = api.post('notebook', { id, content: content || '[]' })
        .then(res => {
          const made = String(res?.id || id).toLowerCase();
          noteIdRef.current = made;
          setNoteId(made);
          rememberNote(scopeKey, made);
          return made;
        })
        .finally(() => { creating.current = null; });
    }
    return creating.current;
  }, [scopeKey]);

  const flush = useCallback(async () => {
    if (!pending.current || savingRef.current) return;
    const content = pending.current;
    savingRef.current = true;
    setSaveState('saving');
    try {
      const existed = !!noteIdRef.current;
      const id = await ensureNote(content);
      if (existed) await api.put(`notebook/${id}`, { content });
      if (pending.current === content) pending.current = null;
      setSaveState(pending.current ? 'dirty' : 'saved');
    } catch {
      setSaveState('error');
    } finally {
      savingRef.current = false;
      if (pending.current) { clearTimeout(timer.current); timer.current = setTimeout(() => flush(), SAVE_DELAY_MS); }
    }
  }, [ensureNote]);

  useEffect(() => () => { clearTimeout(timer.current); flush(); }, [flush]);

  const onChange = useCallback(({ content, isEmpty: empty }) => {
    setIsEmpty(empty);
    // The editor reports its seed on load; that is not an edit.
    if (!seeded.current) { seeded.current = true; return; }
    if (empty && !noteIdRef.current) return; // nothing written yet
    pending.current = content;
    setSaveState('dirty');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(), SAVE_DELAY_MS);
  }, [flush]);

  // Files inside the note: drag-and-drop, paste and the /image menu.
  const uploadFile = useCallback(async (file) => {
    setUploading(n => n + 1);
    try {
      const id = await ensureNote(JSON.stringify(editorRef.current?.document || []));
      return (await uploadNotebookFile(api, id, file)).url;
    } catch (e) { toast.error(e.message || 'Could not add the file.'); throw e; }
    finally { setUploading(n => n - 1); }
  }, [ensureNote, toast]);

  const addInlineFiles = useCallback(async (files) => {
    const editor = editorRef.current;
    if (!editor || !files?.length) return;
    for (const file of files) {
      setUploading(n => n + 1);
      try {
        const id = await ensureNote(JSON.stringify(editor.document || []));
        const up = await uploadNotebookFile(api, id, file);
        let at;
        try { at = editor.getTextCursorPosition().block; } catch { at = editor.document[editor.document.length - 1]; }
        editor.insertBlocks([{ type: up.blockType, props: { url: up.url, name: up.fileName } }], at, 'after');
      } catch (e) {
        toast.error(`${file.name || 'File'}: ${e.message || 'could not be added.'}`);
      } finally { setUploading(n => n - 1); }
    }
  }, [ensureNote, toast]);

  const attachDocuments = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const file of files) {
      const key = `att-${attachKey.current++}`;
      setAttachments(prev => [...prev, { key, fileName: file.name, fileSizeBytes: file.size, contentType: file.type || 'application/octet-stream', uploading: true }]);
      try {
        let ref = null;
        try { ref = await uploadAttachmentToBlob(file); } catch { ref = null; }
        const patch = ref ? { ...ref, uploading: false } : { blobData: await readFileAsBase64(file), uploading: false };
        setAttachments(prev => prev.map(a => (a.key === key ? { ...a, ...patch } : a)));
      } catch {
        toast.error(`Couldn't attach ${file.name}`);
        setAttachments(prev => prev.filter(a => a.key !== key));
      }
    }
  };

  // Handwriting → text. The source page is kept inside the note.
  const readHandwriting = async ({ base64, contentType, keepFile }) => {
    setScanning(true);
    try {
      const { text, markdown } = await runHandwritingOcr({ base64, contentType, getAccessToken });
      const ok = await insertMarkdownAtEnd(editorRef.current, markdown?.trim() ? markdown : text);
      if (keepFile) await addInlineFiles([keepFile]);
      if (ok) toast.success('Text extracted — review it before posting');
      else toast.info(`No text found in the ${contentType === 'application/pdf' ? 'PDF' : 'image'}`);
      return ok;
    } catch (err) {
      toast.error(err.message || 'Text extraction failed');
      return false;
    } finally { setScanning(false); }
  };

  const handleScan = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    if (!file) return;
    let prepared;
    try { prepared = await prepareFileForOcr(file); }
    catch (err) { toast.error(err.message || `Could not read ${ocrFileNoun(file)}`); return; }
    const keepFile = isPdfFile(file) ? file
      : base64ToFile(prepared.base64, `${(file.name || 'scan').replace(/\.[^.]+$/, '')}.jpg`, 'image/jpeg');
    await readHandwriting({ base64: prepared.base64, contentType: prepared.contentType, keepFile });
  };

  const discard = async () => {
    clearTimeout(timer.current);
    pending.current = null;
    const id = noteIdRef.current;
    if (id && !isEmpty && !window.confirm('Discard this note? It moves to Recently deleted in Notes for 30 days.')) return;
    if (id) { try { await api.del(`notebook/${id}`); } catch { /* best-effort */ } }
    rememberNote(scopeKey, null);
    noteIdRef.current = null;
    onClose?.();
  };

  const post = async () => {
    const editor = editorRef.current;
    if (!editor || posting) return;
    const blocks = editor.document;
    if (isEmpty && !attachments.length) return;
    if (attachments.some(a => a.uploading) || uploading) { toast.info('Wait for the files to finish uploading.'); return; }
    document.activeElement?.blur?.(); // iOS: collapse the keyboard before the editor unmounts
    setPosting(true);
    clearTimeout(timer.current);
    try {
      const content = JSON.stringify(blocks);
      pending.current = null;
      const id = await ensureNote(content);
      const token = await getAccessToken();
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 60000);
      let res;
      try {
        res = await fetch('/api/notes', {
          method: 'POST', headers, signal: ctrl.signal,
          body: JSON.stringify({
            ...scope,
            publishNoteId: id,
            content,
            noteDate: noteDate || null,
            authorUserId: authorUserId || null,
            projectId: projectId || null,
            mentionedUserIDs: [],
            attachments: attachments.map(({ key, uploading: _u, ...a }) => a),
            draftId: legacyDraft?.draftId || undefined,
          }),
        });
      } finally { clearTimeout(t); }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || body.error || 'The note could not be posted.');
      if (body.attachmentError) toast.error(body.attachmentError);
      rememberNote(scopeKey, null);
      noteIdRef.current = null;
      setNoteId(null);
      setAttachments([]);
      setIsEmpty(true);
      setProjectId('');
      setSaveState('idle');
      seeded.current = false;
      setSeed({ content: undefined, noteDate: null });
      setNoteDate(todayIsoInZone());
      setEpoch(e => e + 1);
      onPosted?.(body);
    } catch (e) {
      toast.error(e.name === 'AbortError' ? 'Posting took too long — check the feed before trying again.' : e.message);
    } finally { setPosting(false); }
  };

  if (!loaded) return null;

  const saveLabel = !noteId ? null
    : { dirty: 'Editing…', saving: 'Saving…', saved: 'Draft saved', error: 'Not saved — retrying on your next edit', idle: 'Draft saved' }[saveState];
  const control = { fontSize: 12, color: B.text, border: `1px solid ${B.border}`, borderRadius: 4, padding: '2px 6px', fontFamily: 'inherit', background: B.white };

  return (
    <div style={{ border: `1px solid ${B.border}`, borderRadius: 8, background: B.white, marginBottom: 20, width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderBottom: `1px solid ${B.border}`, background: B.surfaceMuted, borderRadius: '8px 8px 0 0', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: B.textMd, fontWeight: 600 }}>
          <Icon name="lock" size={12} /> Only you can see this until you post
        </span>
        {saveLabel && <span style={{ fontSize: 11, color: saveState === 'error' ? B.red : B.textLt }}>· {saveLabel}</span>}
        <div style={{ flex: 1 }} />
        {uploading > 0 && <span style={{ fontSize: 11, color: B.textLt }}>Adding file…</span>}
        <WritingTools editorRef={editorRef} toast={toast} />
      </div>

      {sketching && (
        <div style={{ padding: 12 }}>
          <InkCanvas
            busy={scanning}
            onClose={() => setSketching(false)}
            onKeepDrawing={({ base64 }) => { setSketching(false); addInlineFiles([base64ToFile(base64, 'drawing.jpg', 'image/jpeg')]); }}
            onConvert={async ({ base64 }) => { if (await readHandwriting({ base64, contentType: 'image/jpeg' })) setSketching(false); }}
          />
        </div>
      )}

      <div onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') post(); }}>
        <NoteBodyEditor
          key={`${scopeKey}:${epoch}`}
          editorRef={editorRef}
          initialContent={seed.content}
          onChange={onChange}
          uploadFile={uploadFile}
          resolveFileUrl={resolveFileUrl}
          mathAssist
          autoFocus
          minHeight={120}
          style={{ border: 0, borderRadius: 0 }}
        />
      </div>

      {attachments.length > 0 && (
        <div style={{ padding: '6px 12px', borderTop: `1px solid ${B.border}`, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {attachments.map((a, i) => (
            <div key={a.key ?? i} style={{ display: 'flex', alignItems: 'center', gap: 5, background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 20, padding: '3px 10px', fontSize: 12, opacity: a.uploading ? 0.6 : 1 }}>
              <Icon name="attach" size={12} /> {a.fileName}
              {a.uploading && <span style={{ color: B.textLt }}>uploading…</span>}
              <button type="button" aria-label={`Remove ${a.fileName}`} onClick={() => setAttachments(p => p.filter((_, j) => j !== i))}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: B.textLt, display: 'inline-flex', padding: '0 0 0 4px' }}><Icon name="close" size={13} /></button>
            </div>
          ))}
        </div>
      )}

      {dealId && (
        <div style={{ padding: '2px 12px 0', borderTop: `1px solid ${B.border}` }}>
          <FixedScopeProjectPicker dealId={dealId} dealDetailId={dealDetailId} value={projectId} onChange={setProjectId} />
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, padding: '8px 12px', alignItems: 'center', borderTop: `1px solid ${B.border}`, flexWrap: 'wrap' }}>
        <Btn variant="ghost" size="sm" title="File a document under the note (summarized by AI)" onClick={() => attachInput.current?.click()}><Icon name="attach" size={14} /> Attach</Btn>
        <input ref={attachInput} type="file" hidden multiple onChange={attachDocuments} />
        <Btn variant="ghost" size="sm" title="Put a photo or file inside the note" onClick={() => fileInput.current?.click()}><Icon name="image" size={14} /> Image</Btn>
        <input ref={fileInput} type="file" hidden multiple onChange={e => { const f = [...(e.target.files || [])]; e.target.value = ''; addInlineFiles(f); }} />
        <Btn variant="ghost" size="sm" disabled={scanning} onClick={() => cameraInput.current?.click()}><Icon name="camera" size={14} /> {scanning ? 'Scanning…' : 'Scan'}</Btn>
        <input ref={cameraInput} type="file" hidden accept="image/*" capture="environment" onChange={handleScan} />
        <Btn variant="ghost" size="sm" disabled={scanning} onClick={() => scanInput.current?.click()}><Icon name="image" size={14} /> Scan image or PDF</Btn>
        <input ref={scanInput} type="file" hidden accept="image/*,application/pdf,.pdf" onChange={handleScan} />
        <Btn variant="ghost" size="sm" disabled={scanning} onClick={() => setSketching(v => !v)}><Icon name="pen" size={14} /> Write</Btn>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: B.textMd }} title="The day this note is about — back-date it for a call or meeting that already happened">
          Date
          <input type="date" value={noteDate} max={todayIsoInZone()} onChange={e => setNoteDate(e.target.value)} style={control} />
        </label>
        {internalUsers.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: B.textMd }} title="Attribute this note to the person who took it">
            Author
            <select value={authorUserId} onChange={e => setAuthorUserId(e.target.value)} style={{ ...control, maxWidth: 160 }}>
              {!meUserId && <option value="">Me</option>}
              {internalUsers.map(u => <option key={u.userID} value={u.userID}>{u.displayName}{u.userID === meUserId ? ' (me)' : ''}</option>)}
            </select>
          </label>
        )}
        <div style={{ flex: 1 }} />
        <Btn variant="ghost" size="sm" onClick={discard}>{noteId ? 'Discard' : 'Cancel'}</Btn>
        <Btn size="sm" disabled={posting || (isEmpty && !attachments.length) || attachments.some(a => a.uploading)} onClick={post}>
          {posting ? 'Posting…' : 'Post'}
        </Btn>
      </div>
    </div>
  );
}

export default DealNoteComposer;
